import { capitalize } from 'lodash';

import { EscrowPartyTypeEnum, type IBeneficiary, type IDepositor } from 'src/graphql/schema';
import { SuggestedChangesDocument } from 'src/graphql/actions/suggestChanges/suggestedChanges.query';
import { apolloCache } from 'src/graphql';
import { setOrganizationFullAddress } from 'src/utils/escrow';
import { useSoftwareEscrow } from 'src/context/software-escrow';
import { Table, TableBody, TableCell, TableRow } from '@/components/ui/table';
import { SuggestedChangesView } from 'src/components/SuggestChanges';
import { getOrganizationSuggestedChangesItems } from 'src/containers/SoftwareEscrow/RequestChanges/Party/lib';
import { OrganizationRegistrationNumberTooltip } from 'src/components/Organization';

import type { ISuggestedChangesQuery } from 'src/graphql/actions/suggestChanges/suggestedChanges.query';

export const PartySuggestedChanges = ({ partyType }: { partyType: EscrowPartyTypeEnum }) => {
  const { softwareEscrow } = useSoftwareEscrow();
  const data = apolloCache.readQuery<ISuggestedChangesQuery>({
    query: SuggestedChangesDocument,
    variables: {
      escrowId: softwareEscrow.id,
      changeType: `${capitalize(partyType)}SuggestedChange`,
    },
  });

  if (!data || !data.suggestedChanges.nodes.length) return null;

  const { nodes } = data.suggestedChanges;
  const { payload } = nodes[0];
  const partyPayload = [
    {
      key: 'companyName',
      value: payload?.company_name,
    },
    {
      key: 'companyWebsite',
      value: payload?.company_website === '' ? '-' : payload?.company_website,
    },
    {
      key: 'companyRegistrationNumber',
      value: payload?.company_registration_number === '' ? '-' : payload?.company_registration_number,
    },
    {
      key: 'country',
      value: payload?.country,
    },
    {
      key: 'streetNumber',
      value: payload?.street_number === '' ? '-' : payload?.street_number,
    },
    {
      key: 'postalCode',
      value: payload?.postal_code,
    },
    {
      key: 'region',
      value: payload?.region === '' ? '-' : payload?.region,
    },
    {
      key: 'city',
      value: payload?.city,
    },
    {
      key: 'street',
      value: payload?.street,
    },
  ]
    .filter(({ value }) => Boolean(value))
    .reduce(
      (acc, { key, value }) => {
        acc[key] = value;
        return acc;
      },
      {} as Record<string, unknown>,
    );

  const organizationSuggestedChangesItems = getOrganizationSuggestedChangesItems(partyPayload);

  return <SuggestedChangesView items={organizationSuggestedChangesItems} />;
};

const PartyView = ({ partyType }: { partyType: EscrowPartyTypeEnum }) => {
  const { softwareEscrow } = useSoftwareEscrow();

  const party = softwareEscrow[partyType] as IDepositor | IBeneficiary;
  const address = {
    street: party?.street || '',
    streetNumber: party?.streetNumber || '',
    city: party?.city || '',
    postalCode: party?.postalCode || '',
    region: party?.region || '',
    country: party?.country || '',
  };

  const rows = [
    {
      key: 'companyName',
      label: 'Organization Name',
      value: party?.companyName,
    },
    {
      key: 'companyWebsite',
      label: 'Website',
      value: party?.companyWebsite || '-',
    },
    {
      key: 'companyRegistrationNumber',
      label: (
        <>
          <div className="absolute top-[50%] translate-y-[-50%] right-0 text-foreground">
            <OrganizationRegistrationNumberTooltip />
          </div>
          Registration Number
        </>
      ),
      value: party?.companyRegistrationNumber || '-',
    },
    {
      key: 'address',
      label: 'Address',
      value: setOrganizationFullAddress(address),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="space-y-4">
        <div className="space-y-1">
          <h2 className="text-lg font-bold mb-1">{capitalize(partyType)} organization details</h2>
          <p className="text-muted-foreground text-sm">
            The registered name and address of the entity, as listed in the escrow agreement.
          </p>
        </div>
        <PartySuggestedChanges partyType={partyType} />
      </div>
      <Table>
        <TableBody>
          {rows.map(({ key, label, value }) => (
            <TableRow key={key}>
              <TableCell className="font-medium text-muted-foreground w-[186px] relative">{label}</TableCell>
              <TableCell className="w-[85px]">:</TableCell>
              <TableCell>{value}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export default PartyView;
